import type { Article } from "./types";

export const article: Article = {
  slug: "social-media-fast-guide",
  title: "How to fast from social media without failing by day three",
  h1: "How to fast from social media",
  description:
    "A fast is not a deletion. Here is how to set the length, what to do with the hours it frees, and the four failure modes that end most of them early.",
  targets: [
    "how to do a social media fast",
    "christian fast from social media",
    "giving up social media for lent",
    "social media fast rules",
  ],
  published: "2026-08-17",
  standfirst:
    "Most social media fasts end quietly, around day three, without anyone deciding to end them.",
  takeaways: [
    "A fast has a start, an end and a purpose. Without all three it is not a fast, it is an attempt to use your phone less, and it fails the way those fail.",
    "Pick the shortest length you will actually finish — a completed week teaches you more than an abandoned forty days, and it is the completion that changes anything.",
    "Delete the app but keep the account. Deletion is the friction; account deletion is a separate decision you should not make while hungry.",
    "Name what fills the hours before you start, or the hours will fill themselves with the next-easiest thing on the same phone.",
  ],
  body: [
    {
      type: "p",
      text: "Fasting from social media is one of the few disciplines almost everyone attempts and almost nobody finishes. The pattern is consistent: a decision made on a Sunday, a strong first two days, a small necessary exception on the third — a message, a group, something someone posted — and then no clear moment at which it ended, because there never was one.",
    },
    {
      type: "p",
      text: "That is not a character problem. It is a design problem, and the design has four parts.",
    },
    {
      type: "h2",
      id: "purpose",
      text: "1. Fast toward something, not away from something",
    },
    {
      type: "p",
      text: "A fast in the Christian tradition has never been self-improvement with a religious label on it. It is subtraction that makes room — the hunger is the point, and it is the point because it keeps directing you somewhere. Without that direction you are left with an absence and a lot of free time, which is precisely the condition under which the absence ends.",
    },
    {
      type: "p",
      text: 'So write down the reason before the rules. Not "I use my phone too much" — that is an observation. Something closer to: I want the first ten minutes of my day back. I want to stop checking during dinner. I want to find out what I actually think about, when nothing is being suggested to me.',
    },
    {
      type: "note",
      text: "If you cannot say what the fast is for, you will not be able to say why you are still doing it on day four.",
    },
    {
      type: "h2",
      id: "length",
      text: "2. Choose a length you will finish",
    },
    {
      type: "p",
      text: "The instinct is to pick something impressive — a month, Lent, the rest of the year. The instinct is wrong, and the reason is worth understanding: what changes you is completing one, not attempting a long one. A finished week is an experience you can refer back to. An abandoned forty days is a story about how you cannot do this.",
    },
    {
      type: "ul",
      items: [
        "A week is the shortest fast that gets past the withdrawal and into the quiet. Under that you learn only that the first days are unpleasant.",
        "Forty days is traditional and it is not a beginner's number. Do a week, then two, then decide.",
        "An indefinite fast is not a fast. It has no end to reach, so it has no completion — only a day you stop.",
      ],
    },
    {
      type: "p",
      text: "Pick the end date now and write it somewhere you will see. The date is what turns each difficult evening from a negotiation into a countdown.",
    },
    {
      type: "h2",
      id: "rules",
      text: "3. Write the exceptions down before you need them",
    },
    {
      type: "p",
      text: "Every fast meets a legitimate exception. A family group chat, a church page, a message from someone who does not use anything else. The failure is not the exception — it is deciding on the exception in the moment, because in the moment you are the least qualified person to rule on it.",
    },
    {
      type: "p",
      text: "Decide in advance, in writing, and make the rules boring:",
    },
    {
      type: "ol",
      items: [
        'Which apps are in the fast. Name them. "Social media" is not a list and will quietly shrink.',
        "What you will do about messages. Usually: keep the messaging app, remove the feed apps. Most people find the messages were never the problem.",
        "One check per week, or none. If you need to post something for work or for church, schedule when — and do it from a browser, on a laptop, standing up.",
        'What happens when you break it. Not "start over", which turns one lapse into an ending. Just: resume, same end date.',
      ],
    },
    {
      type: "h2",
      id: "friction",
      text: "4. Make the return expensive, not impossible",
    },
    {
      type: "p",
      text: "Deleting the apps is the standard advice and it is half right. Deletion works because reinstalling takes ninety seconds and a password — enough friction to interrupt an automatic reach, not enough to be a crisis when you genuinely need something.",
    },
    {
      type: "p",
      text: "Do not delete the accounts. That is a much larger decision and one you should never make in the first week of a fast, when everything feels clarifying. If you still want to at the end, the option is still there.",
    },
    {
      type: "p",
      text: "If deletion is too blunt — you need the app for something real — a block with a cost is the version that survives, which is the same property that separates {a-block-that-lasts-from-one-that-does-not} in ordinary daily use.",
      links: {
        "a-block-that-lasts-from-one-that-does-not":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "h2",
      id: "hours",
      text: "What to do with the hours",
    },
    {
      type: "p",
      text: "This is the part people skip and it is the part that decides the outcome. A social media fast frees somewhere between forty minutes and three hours a day. Those hours do not stay empty. They fill with whatever is easiest to reach, and what is easiest to reach is on the same phone you are holding.",
    },
    {
      type: "p",
      text: "Name the replacement before you start and make it specific enough to do without deciding: the Gospel of Mark, ten minutes, after dinner. A walk without headphones. The examen. Writing one page. Praying for three people by name.",
    },
    {
      type: "verse",
      verse: {
        text: "Great multitudes came together to hear and to be healed by him of their infirmities. But he withdrew himself into the desert and prayed.",
        ref: "Luke 5:15–16",
      },
    },
    {
      type: "p",
      text: "The pattern in that verse is not withdrawal from people. It is withdrawal toward prayer, repeatedly, in the middle of the most demanding period of a life. The fast is the withdrawing; the prayer is what makes it worth anything.",
    },
    {
      type: "h2",
      id: "after",
      text: "How it ends",
    },
    {
      type: "p",
      text: "Decide before the end date what happens after it, because the day after a fast is when the whole thing is either kept or lost. Almost nobody returns to exactly what they had; almost everybody returns to more than they intended.",
    },
    {
      type: "p",
      text: "The useful version is to return with one rule kept. Not the whole fast — one thing. No feeds before prayer. Nothing after nine. One app back, not four. That single retained rule is the entire yield of the exercise, and if you take nothing else from a week without it, take that.",
    },
    {
      type: "p",
      text: "{Bible-Shield} exists for the part after the fast: the apps come back, and they close again at the hours you chose, and what stands between you and them is a short prayer rather than a decision you have to keep making.",
      links: { "Bible-Shield": "/" },
    },
    {
      type: "p",
      text: "The part of this that lasts past the fast is usually about comparison rather than about time, and that has its own collection: {ten-passages-on-comparison}, including the verse that calls the whole practice a failure of understanding rather than a failure of character.",
      links: {
        "ten-passages-on-comparison": "/verses/bible-verses-about-comparison",
      },
    },
  ],
  faq: [
    {
      q: "How long should a social media fast be?",
      a: "A week, for a first one. It is long enough to get past the withdrawal and short enough that you will finish it, and finishing is the part that changes anything. Forty days is traditional and is not a beginner's number.",
    },
    {
      q: "Should I tell people I am fasting?",
      a: "Tell the two or three who would otherwise worry, and nobody else. Matthew 6 is pointed about fasting performed for an audience, and an announcement also turns a private discipline into a public commitment you then have to manage.",
    },
    {
      q: "What if I break the fast?",
      a: "Resume, same end date. Starting over converts one lapse into an ending, which is how most fasts actually die — not from the lapse, but from the rule about the lapse.",
    },
    {
      q: "Is it better to delete the apps or block them?",
      a: "Delete if you can do without them entirely; block with a real cost if you cannot. Both work by making the return expensive rather than impossible. What does not work is leaving them installed and relying on deciding each time.",
    },
  ],
  related: ["screen-time-limits-dont-work", "morning-quiet-time-routine"],
};
